import { useEffect, useState } from 'react';
import { X, Info } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * LegalAckModal — one-time contextual acknowledgment shown before AI Coach,
 * Assessments, or Support Coach.  Once the user taps "I Understand" the
 * acknowledgment is persisted to localStorage and never shown again.
 *
 * Props:
 *   storageKey  unique key for this acknowledgment  (required)
 *   title       modal title                        (required)
 *   body        short body copy                    (required)
 *   readMoreTo  optional route to the full page in Trust & Safety Center
 *   readMoreLabel  optional custom label for the CTA (default "Read the full disclosure")
 */
export default function LegalAckModal({ storageKey, title, body, readMoreTo, readMoreLabel }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const seen = localStorage.getItem(storageKey);
      if (!seen) setOpen(true);
    } catch { /* ignore */ }
  }, [storageKey]);

  if (!open) return null;

  const acknowledge = () => {
    try { localStorage.setItem(storageKey, new Date().toISOString()); } catch { /* ignore */ }
    setOpen(false);
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-3 sm:p-6"
         style={{ background: 'rgba(11, 25, 44, 0.6)', backdropFilter: 'blur(6px)' }}
         data-testid={`legal-ack-${storageKey}`}
         role="dialog"
         aria-modal="true"
         aria-labelledby={`legal-ack-title-${storageKey}`}>
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 fade-in duration-300">
        <div className="p-6 lg:p-7">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-gold/10 border border-gold/20 flex items-center justify-center shrink-0">
                <Info size={16} className="text-gold" strokeWidth={2}/>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.28em] text-gold font-semibold">Before you begin</p>
                <h3 id={`legal-ack-title-${storageKey}`} className="font-heading text-xl text-navy mt-0.5 leading-tight">{title}</h3>
              </div>
            </div>
            <button type="button" onClick={acknowledge} aria-label="Dismiss"
                    data-testid={`legal-ack-close-${storageKey}`}
                    className="text-navy/40 hover:text-navy transition-smooth p-1 -m-1 shrink-0">
              <X size={16}/>
            </button>
          </div>

          <p className="text-sm text-navy/75 mt-4 leading-relaxed">{body}</p>

          <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-2 mt-5">
            {readMoreTo && (
              <Link to={readMoreTo}
                    data-testid={`legal-ack-readmore-${storageKey}`}
                    className="text-center text-xs font-semibold text-navy/70 hover:text-navy underline decoration-navy/20 hover:decoration-navy/60 underline-offset-4 px-3 py-2">
                {readMoreLabel || 'Read the full disclosure'}
              </Link>
            )}
            <button type="button"
                    onClick={acknowledge}
                    data-testid={`legal-ack-accept-${storageKey}`}
                    className="flex-1 rounded-full bg-navy text-white font-semibold text-sm py-3 hover:brightness-110 transition-smooth">
              I Understand
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
